import { useState } from 'react';
import { NavBar } from '../components/NavBar.tsx';
import { Stub } from '../components/Stub.tsx';
import { useMe } from '../api/me.ts';
import { useFriends, useGroup, useGroups, useSheet } from '../api/rooms.ts';
import { isLive } from '../api/client.ts';
import type { Entry, ItemKind, Person, PrayerNote, Sheet } from '../domain.ts';
import { FriendPopup } from './partner/FriendPopup.tsx';
import { GroupList } from './room/GroupList.tsx';
import { GroupForm } from './room/GroupForm.tsx';
import { SheetList } from './room/SheetList.tsx';
import { SheetForm } from './room/SheetForm.tsx';
import { SheetView } from './room/SheetView.tsx';
import { EntryWriter } from './room/EntryWriter.tsx';
import { SaveToBook } from './room/SaveToBook.tsx';
import { SharedNote } from './room/SharedNote.tsx';

/**
 * F1 ~ F4-5 — Prayer Room.
 *
 * 화면 흐름 (설계 2026-08-27-1-22 기준)
 *
 *   F1   친구 · 모임 리스트   탭을 열면 여기
 *     ＋ → F2 모임 만들기
 *     모임 → F3,  친구 → D2 팝업 → F1-1 노트
 *   F3   기록지 리스트
 *     ⋯ → F3-1 메뉴 → F3-1-1 수정 · F3-1-2/3 나가기
 *     얼굴 줄 → F3-2 구성원 창 → F3-2-1 편집
 *     ＋ → F3-3 기록지 만들기
 *     카드 → F4
 *   F4   기록지
 *     연필 → F4-2 적기 → F4-2-1 Prayer Book 으로 저장
 *     이름 → D2 · D2-1,  내용 → F4-3 펼쳐 보기
 *     ⋯ → F4-4 메뉴 → F4-4-1 수정 · F4-4-2 삭제
 *     ＋ → F4-5 항목 추가
 *
 * **여기서는 목업으로 떨어지지 않습니다.** 노트는 토큰이 없어도 디자인을
 * 볼 수 있게 목업을 두었지만, 모임은 «누가 그룹장인가 · 누가 이 줄을 적을 수
 * 있는가»가 서버가 정하는 값이라 목업으로는 화면의 절반이 거짓이 됩니다.
 */

type View =
  | { at: 'list' }
  | { at: 'newGroup' }
  | { at: 'group'; groupId: string }
  | { at: 'editGroup'; groupId: string }
  | { at: 'newSheet'; groupId: string }
  | { at: 'sheet'; groupId: string; sheetId: string }
  | { at: 'editSheet'; groupId: string; sheetId: string }
  | { at: 'note'; note: PrayerNote; author: string; back: View };

/** 기록지 위에 떠 있는 것 */
type Layer =
  | { at: 'none' }
  | { at: 'write'; entry: Entry; kind: ItemKind }
  | { at: 'toBook'; entry: Entry };

export function Room() {
  const [view, setView] = useState<View>({ at: 'list' });
  const [layer, setLayer] = useState<Layer>({ at: 'none' });
  const [popup, setPopup] = useState<Person | null>(null);

  const { userId } = useMe();
  const groups = useGroups();
  const friends = useFriends();

  const groupId = 'groupId' in view ? view.groupId : null;
  const sheetId = 'sheetId' in view ? view.sheetId : null;
  const group = useGroup(groupId);
  const sheet = useSheet(sheetId);

  /** 기록지가 바뀌면 들고 있는 것을 갈아 줍니다 — 다시 부르지 않고 */
  const [fresh, setFresh] = useState<Sheet | null>(null);
  const live = fresh ?? sheet.data;

  if (!isLive()) {
    return (
      <div className="screen">
        <NavBar title="Prayer Room" large />
        <div className="screen__body">
          <Stub
            icon="room"
            title="로그인이 필요합니다"
            note="모임은 누가 그룹장이고 누가 어느 줄을 적을 수 있는지를 서버가 들고 있어 목업으로 보여드릴 수 없습니다. 앱에서 카카오로 로그인하면 열립니다."
            screens="F1 ~ F4-5"
          />
        </div>
      </div>
    );
  }

  /* ── 남의 노트 읽기 — F1-1 ──────────────────────────────── */

  if (view.at === 'note') {
    const back = view.back;
    return (
      <SharedNote note={view.note} author={view.author} onBack={() => setView(back)} />
    );
  }

  /* ── F2 · F3-1-1 모임 만들기 · 수정 ─────────────────────── */

  if (view.at === 'newGroup') {
    return (
      <GroupForm
        onCancel={() => setView({ at: 'list' })}
        onSaved={(made) => {
          groups.reload();
          setView({ at: 'group', groupId: made.groupId });
        }}
      />
    );
  }

  if (view.at === 'editGroup' && group.data) {
    return (
      <GroupForm
        group={group.data}
        onCancel={() => setView({ at: 'group', groupId: view.groupId })}
        onSaved={() => {
          group.reload();
          groups.reload();
          setView({ at: 'group', groupId: view.groupId });
        }}
      />
    );
  }

  /* ── F3-3 · F4-4-1 기록지 만들기 · 수정 ─────────────────── */

  if (view.at === 'newSheet' && group.data) {
    return (
      <SheetForm
        group={group.data}
        onCancel={() => setView({ at: 'group', groupId: view.groupId })}
        onSaved={(made) => {
          setFresh(made);
          setView({ at: 'sheet', groupId: view.groupId, sheetId: made.sheetId });
        }}
      />
    );
  }

  if (view.at === 'editSheet' && group.data && live) {
    const { groupId: g, sheetId: s } = view;
    return (
      <SheetForm
        group={group.data}
        sheet={live}
        onCancel={() => setView({ at: 'sheet', groupId: g, sheetId: s })}
        onSaved={(next) => {
          setFresh(next);
          setView({ at: 'sheet', groupId: g, sheetId: s });
        }}
      />
    );
  }

  /* ── F4-2-1 Prayer Book 으로 저장 ───────────────────────── */

  if (layer.at === 'toBook') {
    const entry = layer.entry;
    return (
      <SaveToBook
        entry={entry}
        onCancel={() => setLayer({ at: 'write', entry, kind: 'PRAYER_NOTE' })}
        onSaved={() => {
          setLayer({ at: 'none' });
          setFresh(null);
          sheet.reload();
        }}
      />
    );
  }

  /* ── F4 기록지 ──────────────────────────────────────────── */

  if (view.at === 'sheet') {
    const { groupId: g, sheetId: s } = view;
    return (
      <>
        {live ? (
          <SheetView
            sheet={live}
            meId={userId}
            ownerId={group.data?.ownerId ?? ''}
            onBack={() => {
              setFresh(null);
              setView({ at: 'group', groupId: g });
            }}
            onWrite={(entry, kind) => setLayer({ at: 'write', entry, kind })}
            onPerson={setPopup}
            onOpenNote={(note) =>
              setView({
                at: 'note',
                note,
                author: note.author,
                back: { at: 'sheet', groupId: g, sheetId: s },
              })
            }
            onEdit={() => setView({ at: 'editSheet', groupId: g, sheetId: s })}
            onDeleted={() => {
              setFresh(null);
              setView({ at: 'group', groupId: g });
            }}
            onChanged={setFresh}
          />
        ) : (
          <div className="screen">
            <NavBar
              title="기록지"
              start={{ icon: 'back', label: '돌아가기', onClick: () => setView({ at: 'group', groupId: g }) }}
            />
            <div className="screen__body">
              <Loadingish loading={sheet.loading} error={sheet.error} onRetry={sheet.reload} />
            </div>
          </div>
        )}

        {layer.at === 'write' && live && (
          <EntryWriter
            entry={freshEntry(live, layer.entry.entryId) ?? layer.entry}
            kind={layer.kind}
            sheetTitle={live.title}
            mine={layer.entry.member.userId === userId}
            onClose={() => setLayer({ at: 'none' })}
            onSaved={(next) => {
              setFresh(next);
              setLayer({ at: 'none' });
            }}
            onSaveToBook={() =>
              setLayer({ at: 'toBook', entry: freshEntry(live, layer.entry.entryId) ?? layer.entry })
            }
          />
        )}

        {popup && (
          <FriendPopup
            person={popup}
            context={group.data ? `${group.data.name} 구성원` : undefined}
            onClose={() => setPopup(null)}
            onAdded={friends.reload}
          />
        )}
      </>
    );
  }

  /* ── F3 기록지 리스트 ───────────────────────────────────── */

  if (view.at === 'group') {
    const g = view.groupId;
    return (
      <>
        {group.data ? (
          <SheetList
            group={group.data}
            onBack={() => setView({ at: 'list' })}
            onOpenSheet={(id) => {
              setFresh(null);
              setView({ at: 'sheet', groupId: g, sheetId: id });
            }}
            onNewSheet={() => setView({ at: 'newSheet', groupId: g })}
            onEditGroup={() => setView({ at: 'editGroup', groupId: g })}
            onLeft={() => {
              groups.reload();
              setView({ at: 'list' });
            }}
            onChanged={() => {
              group.reload();
              groups.reload();
            }}
          />
        ) : (
          <div className="screen">
            <NavBar
              title="모임"
              start={{ icon: 'back', label: '모임 목록', onClick: () => setView({ at: 'list' }) }}
            />
            <div className="screen__body">
              <Loadingish loading={group.loading} error={group.error} onRetry={group.reload} />
            </div>
          </div>
        )}
      </>
    );
  }

  /* ── F1 친구 · 모임 리스트 ──────────────────────────────── */

  return (
    <>
      <GroupList
        groups={groups.data}
        friends={friends.data}
        loading={groups.loading || friends.loading}
        error={groups.error ?? friends.error}
        onReload={() => {
          groups.reload();
          friends.reload();
        }}
        onOpenGroup={(id) => setView({ at: 'group', groupId: id })}
        onOpenFriend={setPopup}
        onNewGroup={() => setView({ at: 'newGroup' })}
      />

      {popup && (
        <FriendPopup person={popup} onClose={() => setPopup(null)} onAdded={friends.reload} />
      )}
    </>
  );
}

/**
 * 시트를 다시 읽은 뒤에도 같은 줄을 가리키게 합니다.
 *
 * F4-2 를 열어 둔 채 저장하면 기록지가 통째로 새로 옵니다. 그때 들고 있던
 * 옛 줄을 계속 쓰면 방금 저장한 것이 화면에 안 보입니다 — 식별자로 다시
 * 찾아 씁니다.
 */
function freshEntry(sheet: Sheet, entryId: string): Entry | undefined {
  for (const item of sheet.items) {
    const found = item.entries.find((e) => e.entryId === entryId);
    if (found) return found;
  }
  return undefined;
}

/** 아직 못 읽었거나 실패했을 때의 본문 한 조각 */
function Loadingish({
  loading,
  error,
  onRetry,
}: {
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  if (loading) {
    return (
      <div className="empty">
        <p className="empty__title">불러오는 중</p>
      </div>
    );
  }
  return (
    <div className="empty">
      <p className="empty__title">{error ?? '찾을 수 없습니다'}</p>
      <p className="empty__hint">
        <button type="button" className="mpop__action" onClick={onRetry}>
          다시 시도
        </button>
      </p>
    </div>
  );
}
